/**
 * SalesPreviewDetailView — /preview/sales/detail?slip=  판매 한 건(시안, 10-28 회의용 · 회의 2026-10-21 §9)
 *
 * 두 칸(현민 2026-10-27 A·A·A) —
 *   왼쪽: 머리(판매 번호 · 거래처 · 담당자 · 진행 상태) · 금액(판매 · 원가 · 마진) · 단계 줄 여섯(일직선, 칸마다 넘기는 쪽 ·
 *         지금 칸 밑에 다음 단계 버튼 — 시안이라 안 눌림) · 품목 전부(파트넘버 → 제품 목록 찾기)
 *   오른쪽: 쌓이는 기록 — 타임라인 한 줄씩(누가 · 언제 · 종류 · 글). 시안이라 저장 안 함(새로고침하면 사라진다)
 * 세금계산서는 목록의 행마다(상세엔 없다) · 임대는 뒤에 따로(현민 2026-10-27).
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import type { SalesPreviewDetail, SalesPreviewLine } from '../api/client';
import { assert } from '../lib/assert';
import { getCurrentEmail } from '../lib/auth';
import { escapeHtml, fmtNumber } from '../lib/cells';
import {
  SALES_KIND_WORD, SALES_NOTE_KINDS, SALES_NOTE_KIND_WORD, SALES_STEPS, SALES_STEP_ACTOR_WORD,
  itemNameOf, marginRateOf, productSearchHrefOf, salesKindOf, stepIndexOf, wonText,
} from './sales-preview/words';
import type { SalesNote, SalesNoteKind } from './sales-preview/words';

interface SalesPreviewDetailData {
  slipNo: string;                        // 이카운트 일자-No. — 주소의 ?slip=
  detail: SalesPreviewDetail | null;     // 받기 전엔 null
  notes: SalesNote[];                    // 쌓인 기록 — 최신이 앞. 시안이라 이 화면에만
  loading: boolean;
  error: string;
}

export class SalesPreviewDetailView extends SolaView<SalesPreviewDetailData> {
  override events = {
    click: { '@note-add': 'handleNoteAdd' },
  };

  constructor(params: { slipNo: string }) {
    super();
    this.data = { slipNo: params.slipNo, detail: null, notes: [], loading: true, error: '' };
  }

  override getTitle(): string {
    return `판매 ${this.data.slipNo} (시안) — HCLTech`;
  }

  override onMount(): void {
    void this.load();
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-sales-preview pv-page">
        <section class="page-body pv-body">${this.renderBody()}</section>
      </div>`;
  }

  private async load(): Promise<void> {
    if (this.data.slipNo === '') {
      this.data = { ...this.data, loading: false, error: '주소에 ?slip=(일자-No.)이 없습니다' };
      this.refreshRegion('.pv-body', () => this.renderBody());
      return;
    }
    try {
      const detail = await api.fetchSalesPreview(this.data.slipNo);
      this.data = { ...this.data, detail, loading: false };
    } catch (e) {
      this.data = { ...this.data, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.refreshRegion('.pv-body', () => this.renderBody());
  }

  // ─── 그리기 ───
  private renderBody(): string {
    const { detail, loading, error } = this.data;
    if (loading) return '<p class="text-muted">불러오는 중…</p>';
    if (detail === null) return `<p class="text-danger">불러오지 못했습니다 — ${escapeHtml(error)}</p>`;

    return `
      <p class="sp-demo-note">시안 — 이카운트 판매 실데이터. 진행 단계는 이카운트 상태 "${escapeHtml(detail.progress_status ?? '')}" 로 짐작해 보인 것, 버튼 · 기록은 저장되지 않는다.</p>
      <div class="sp-detail-grid">
        <div class="sp-detail-main">
          ${renderHead(detail)}
          ${renderSteps(detail)}
          ${renderLines(detail.lines)}
        </div>
        <aside class="sp-detail-side">${this.renderNotes()}</aside>
      </div>`;
  }

  /** 쌓이는 기록 — 맨 위 입력(종류 · 글), 아래 타임라인 최신순 */
  private renderNotes(): string {
    const options = SALES_NOTE_KINDS.map((kind) => `<option value="${kind}">${SALES_NOTE_KIND_WORD[kind]}</option>`).join('');
    const lines = this.data.notes.length === 0
      ? '<p class="text-muted sp-note-empty">아직 기록이 없습니다 — 제품팀의 세팅 내역 · 요청사항 · 이슈, 출고 뒤 기사님 번호 · 송장이 여기 한 줄씩 쌓인다</p>'
      : `<ol class="sp-timeline">${this.data.notes.map(renderNoteLine).join('')}</ol>`;
    return `
      <section class="form-card sp-notes">
        <h2 class="sp-card-title">기록 ${this.data.notes.length}</h2>
        <div class="sp-note-form">
          <select class="input sp-note-kind">${options}</select>
          <textarea class="input sp-note-text" rows="2" placeholder="예: RAID 1 구성, iLO 계정 전달 · 기사님 010-… · CJ 1234-5678-9012"></textarea>
          <button type="button" class="btn-primary sp-note-add" data-action="note-add">기록 남기기</button>
        </div>
        <div class="sp-notes-list">${lines}</div>
      </section>`;
  }

  // ─── 핸들러 ───
  /** 기록 한 줄 남기기 — 시안이라 이 화면 상태에만 */
  handleNoteAdd(_e: Event, el: HTMLElement): void {
    const form = el.closest('.sp-note-form');
    assert(form !== null, '기록 남기기 버튼은 .sp-note-form 안에 있다 — renderNotes 가 그렸다');
    const kindSelect = form.querySelector<HTMLSelectElement>('.sp-note-kind');
    const textArea = form.querySelector<HTMLTextAreaElement>('.sp-note-text');
    assert(kindSelect !== null && textArea !== null, '기록 입력 칸은 종류 셀렉트와 글 칸 둘 — renderNotes 가 그렸다');
    const text = textArea.value.trim();
    if (text === '') return;

    const who = getCurrentEmail();
    const note: SalesNote = {
      kind: kindSelect.value as SalesNoteKind,
      text,
      who: who === null ? '나' : who,
      at: new Date().toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' }),
    };
    this.data = { ...this.data, notes: [note, ...this.data.notes] };
    this.refreshRegion('.sp-detail-side', () => this.renderNotes());
  }
}

/** 머리 — 판매 번호 · 거래처 · 상태 · 판매일 · 담당 + 금액 셋 */
function renderHead(detail: SalesPreviewDetail): string {
  const kind = salesKindOf(detail.progress_status);
  const rate = marginRateOf(detail);
  return `
    <section class="form-card sp-head sp-kind-${kind}">
      <div class="sp-line sp-line-head">
        <span class="sp-no mono">${escapeHtml(detail.slip_no)}</span>
        <h1 class="sp-customer">${detail.customer_name === null ? '(거래처 없음)' : escapeHtml(detail.customer_name)}</h1>
        <span class="sp-status sp-status-${kind}" title="이카운트: ${escapeHtml(detail.progress_status ?? '')}">${SALES_KIND_WORD[kind]}</span>
        <span class="sp-spacer"></span>
        <span class="text-muted">판매일 ${escapeHtml(detail.sold_date)}</span>
        <span>담당 ${detail.manager_name === null ? '<span class="text-muted">—</span>' : escapeHtml(detail.manager_name)}</span>
      </div>
      <div class="sp-money-grid">
        <div><small>판매(공급가액)</small><b>${wonText(detail.supply_amount)}</b></div>
        <div><small>원가(추정)</small><b>${wonText(detail.cost_amount)}</b></div>
        <div><small>마진</small><b class="${detail.margin_amount !== null && detail.margin_amount < 0 ? 'sp-negative' : ''}">${wonText(detail.margin_amount)}${rate === null ? '' : ` <span class="text-muted">(${rate.toFixed(1)}%)</span>`}</b></div>
      </div>
      <p class="sp-money-note text-muted">원가 = 같은 파트넘버 · 컨디션의 판매일 이전 최근 구매 단가(추정) — 실물이 판매에 묶이면 그 실물의 원가로 확정된다.${detail.cost_missing_count > 0 ? ` <span class="sp-partial">품목 ${detail.line_count}줄 중 ${detail.cost_missing_count}줄은 구매 기록이 없어 원가 · 마진에서 뺐다</span>` : ''}</p>
    </section>`;
}

/** 단계 줄 — 여섯 일직선, 칸마다 넘기는 쪽. 지금 칸 밑에 다음 단계 버튼(시안 — 안 눌림) */
function renderSteps(detail: SalesPreviewDetail): string {
  const current = stepIndexOf(salesKindOf(detail.progress_status));
  const steps = SALES_STEPS.map((step, index) => {
    const state = index < current ? 'done' : index === current ? 'current' : 'todo';
    return `
      <li class="sp-step sp-step-${state}">
        <span class="sp-step-no">${index < current ? '✓' : index + 1}</span>
        <span class="sp-step-label">${step.label}<small class="sp-step-actor sp-actor-${step.actor}">${SALES_STEP_ACTOR_WORD[step.actor]}</small></span>
      </li>`;
  }).join('');
  const next = SALES_STEPS[current + 1];
  const button = next === undefined
    ? '<p class="sp-step-done-note">납품 완료 — 남은 단계가 없다</p>'
    : `<button type="button" class="btn-primary sp-step-next" disabled title="시안 — 눌리지 않는다">다음: ${next.label} — ${SALES_STEP_ACTOR_WORD[next.actor]}</button>`;
  return `
    <section class="form-card sp-steps-card">
      <ol class="sp-steps">${steps}</ol>
      <div class="sp-step-action">${button}</div>
    </section>`;
}

/** 품목 전부 — 원가 단가는 근거 구매 전표와 함께 */
function renderLines(lines: readonly SalesPreviewLine[]): string {
  const rows = lines.map((line) => `
    <tr>
      <td class="pl-no">${line.line_seq}</td>
      <td class="mono">${line.part_number === null ? '—' : `<a href="${productSearchHrefOf(line.part_number)}" target="_blank" rel="noopener" title="제품 목록에서 이 파트넘버 찾기(새 탭)">${escapeHtml(line.part_number)}</a>`}</td>
      <td class="sp-spec" title="${escapeHtml(line.spec ?? '')}">${escapeHtml(itemNameOf(line))}<div class="text-muted">${escapeHtml(line.item_group ?? '')}${line.vendor_name === null ? '' : ` · ${escapeHtml(line.vendor_name)}`}</div></td>
      <td>${escapeHtml(line.condition_name ?? '')}</td>
      <td class="pl-qty">${line.quantity === null ? '—' : fmtNumber(line.quantity)}</td>
      <td class="pl-qty">${wonText(line.unit_price)}</td>
      <td class="pl-qty">${wonText(line.supply_amount)}</td>
      <td class="pl-qty">${line.cost_unit_price === null ? '<span class="text-muted">—</span>' : `${wonText(line.cost_unit_price)}<div class="text-muted sp-evidence">${escapeHtml(line.cost_slip_no ?? '')}</div>`}</td>
      <td class="pl-qty ${line.margin_amount !== null && line.margin_amount < 0 ? 'sp-negative' : ''}">${wonText(line.margin_amount)}</td>
    </tr>`).join('');
  return `
    <section class="form-card">
      <h2 class="sp-card-title">품목 ${lines.length}</h2>
      <div class="sp-table-wrap"><table class="pv-table">
        <thead><tr><th class="pl-no">#</th><th>파트넘버</th><th>품목</th><th>컨디션</th><th>수량</th><th>판매 단가</th><th>판매(공급가)</th><th title="같은 파트넘버 · 컨디션의 판매일 이전 최근 구매 단가 — 밑에 그 구매 전표">원가 단가(추정)</th><th>마진</th></tr></thead>
        <tbody>${rows}</tbody>
      </table></div>
    </section>`;
}

/** 기록 한 줄 — 종류 칩 · 누가 · 언제 · 글 */
function renderNoteLine(note: SalesNote): string {
  return `
    <li class="sp-note sp-note-${note.kind}">
      <div class="sp-note-meta"><span class="sp-note-chip">${SALES_NOTE_KIND_WORD[note.kind]}</span><span class="sp-note-who" title="${escapeHtml(note.who)}">${escapeHtml(note.who.split('@')[0] ?? note.who)}</span><span class="text-muted">${escapeHtml(note.at)}</span></div>
      <div class="sp-note-text-body">${escapeHtml(note.text)}</div>
    </li>`;
}
